import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function SupportChat() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    loadChat();
    markAsRead();

    // Start polling for new messages every 10 seconds
    pollIntervalRef.current = setInterval(() => {
      pollNewMessages();
    }, 10000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const loadChat = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/support/chat`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessages(res.data.messages || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading chat:', error);
      setLoading(false);
    }
  };

  const pollNewMessages = async () => {
    try {
      // If no messages yet, reload the entire chat
      if (messages.length === 0) {
        await loadChat();
        return;
      }

      const lastMessage = messages[messages.length - 1];
      const lastTimestamp = new Date(lastMessage.createdAt).getTime();

      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE}/support/messages?since=${lastTimestamp}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.messages && res.data.messages.length > 0) {
        setMessages(prev => [...prev, ...res.data.messages]);
        markAsRead();
      }
    } catch (error) {
      console.error('Error polling messages:', error);
    }
  };

  const markAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/support/mark-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleSend = async () => {
    if ((!inputText.trim() && attachments.length === 0) || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE}/support/messages`,
        {
          text: inputText.trim(),
          attachments
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessages(prev => [...prev, res.data.message]);
      setInputText('');
      setAttachments([]);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Не удалось отправить сообщение');
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Файл слишком большой.\n\nМаксимальный размер: 10 МБ\nРазмер вашего файла: ' + (file.size / 1024 / 1024).toFixed(2) + ' МБ');
      e.target.value = '';
      return;
    }

    // Check file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      alert('Неверный формат файла.\n\nРазрешены только:\n- JPG/JPEG изображения\n- PNG изображения\n- GIF анимации\n- PDF документы');
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API_BASE}/support/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setAttachments(prev => [...prev, res.data]);
      e.target.value = ''; // Clear input
    } catch (error) {
      console.error('Error uploading file:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Не удалось загрузить файл';
      alert(errorMsg);
      e.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (d.toDateString() === today.toDateString()) {
      return 'Сегодня';
    } else if (d.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    } else {
      return d.toLocaleDateString('ru-RU');
    }
  };

  const renderAttachment = (attachment) => {
    const baseUrl = API_BASE.replace('/api', '');

    return (
      <Chip
        label={attachment.filename}
        component="a"
        href={`${baseUrl}${attachment.url}`}
        target="_blank"
        clickable
        sx={{ mt: 1 }}
      />
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Group messages by date
  const groupedMessages = (messages || []).reduce((groups, message) => {
    const date = formatDate(message.createdAt);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 4 }}>
      <Paper
        elevation={0}
        sx={{
          height: { xs: 'calc(100vh - 140px)', md: 'calc(100vh - 200px)' },
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: '16px', md: '24px' },
          overflow: 'hidden',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
            color: '#fff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'rgba(255, 255, 255, 0.3)' }}>Т</Avatar>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Техподдержка ThinkLink
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Обычно отвечаем в течение часа
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Messages */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 2,
            background: '#f7fafc',
          }}
        >
          {Object.entries(groupedMessages).map(([date, dateMessages]) => (
            <Box key={date}>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Chip label={date} size="small" sx={{ bgcolor: 'rgba(0, 0, 0, 0.08)' }} />
              </Box>

              {dateMessages.map((message, idx) => {
                const isRespondent = message.senderRole === 'respondent';

                return (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: isRespondent ? 'flex-end' : 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: '70%',
                        display: 'flex',
                        flexDirection: isRespondent ? 'row-reverse' : 'row',
                        gap: 1,
                        alignItems: 'flex-end',
                      }}
                    >
                      {!isRespondent && (
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#cbd5e0' }}>
                          Т
                        </Avatar>
                      )}

                      <Box>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderRadius: isRespondent ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                            background: isRespondent
                              ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                              : '#d1d5db',
                            color: isRespondent ? '#fff' : '#2d3748',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                          }}
                        >
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {message.text}
                          </Typography>

                          {message.attachments && message.attachments.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              {message.attachments.map((att, attIdx) => (
                                <Box key={attIdx}>{renderAttachment(att)}</Box>
                              ))}
                            </Box>
                          )}
                        </Paper>

                        <Typography
                          variant="caption"
                          sx={{
                            color: '#a0aec0',
                            display: 'block',
                            mt: 0.5,
                            textAlign: isRespondent ? 'right' : 'left',
                          }}
                        >
                          {formatTime(message.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input */}
        <Box sx={{ p: 2, background: '#fff', borderTop: '1px solid rgba(0, 0, 0, 0.08)' }}>
          {/* Attachments preview */}
          {attachments.length > 0 && (
            <Box sx={{ mb: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {attachments.map((att, idx) => (
                <Chip
                  key={idx}
                  label={att.filename}
                  onDelete={() => removeAttachment(idx)}
                  size="small"
                  sx={{ maxWidth: 200 }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,application/pdf"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />

            <IconButton
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || sending}
              sx={{ color: '#0984e3' }}
            >
              {uploading ? <CircularProgress size={24} /> : <AttachFileIcon />}
            </IconButton>

            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Напишите сообщение..."
              disabled={sending}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                },
              }}
            />

            <IconButton
              onClick={handleSend}
              disabled={(!inputText.trim() && attachments.length === 0) || sending}
              sx={{
                background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                color: '#fff',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0984e3 0%, #0652DD 100%)',
                },
                '&.Mui-disabled': {
                  background: '#cbd5e0',
                  color: '#fff',
                },
              }}
            >
              {sending ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : <SendIcon />}
            </IconButton>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}
